/* eslint-disable react/jsx-one-expression-per-line */
import * as React from "react";
import { Link } from "react-router-dom";
import Collapsible from "react-collapsible";

// Components
import Container from "../components/Container/Container";
import Footer from "../components/Footer/Footer";
import Button from "../components/Button/Button";

const HowItWorksPage: React.FC = () => {
  return (
    <>
      <Container>
        <div className="w-11/12 xxl:w-full mx-auto pb-4">
          <h1 className="text-xl md:text-38 lg:text-46 text-dw-blue font-semibold font-serif mb-2">
            How do I use the Media Resilience Scanner?
          </h1>
          <p className="font-bold text-dw-grey-13 mb-4 md:text-18">
            The Media Resilience Scanner guides you through a number of
            different topics such as basic planning, hostile environment,
            digital threats, natural and man-made disaster and internal crisis.
            Start with basic planning. Only then fill in the other topics
            relevant to your organization. The Media Resilience Scanner guides
            you step by step to create your crisis plan. You have the option to
            click on "Save progress" to continue the process at a later point.
            After you’ve answered the required questions, the system will
            provide you with a crisis plan by clicking on "Get results" and
            "Finish crisis plan".
            <br />
            <br />
            We have also listed the most frequently asked questions below about
            how to use the Media Resilience Scanner.
          </p>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="Do I have to register an account with the Media Resilience Scanner?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                No, you don’t have to register with the Media Resilience Scanner
                using any personal information (such as your name, email address
                or phone number). Only you decide what information you would
                like to provide. For security reasons, the Media Resilience
                Scanner does not store any user data on the server.{" "}
                <Link to="/faq/security" className="font-bold underline">
                  See here
                </Link>{" "}
                how the Media Resilience Scanner uses your data to produce your
                results.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="What can I expect from the Media Resilience Scanner?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                You will receive a complete, tailor-made crisis plan based on
                the answers you submitted for each subject. The document will be
                provided as a PDF.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger='Why does "This field is required" sometimes pop up?'
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                Some questions are required to be answered. In these cases,
                "This field is required" pops up. These statements have to be
                answered to get your individual crisis plan.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="There are crisis plans available for five different topics in total. Do I have to fill them all out to get my results?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                To create a comprehensive crisis plan start with "Basic
                planning". Then choose and fill in the other topics relevant to
                your organization, such as "Hostile environment", "Natural and
                human-made disaster", "Digital threats" or "Internal crisis".
                You do not have to fill them all out to get results. The more
                information you provide, however, the more comprehensive the
                crisis plan will be. Answering the questions will provide you
                with the crisis plan by clicking on "Get results" and "Finish
                crisis plan".
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="Which topics do you recommend filling out?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                This really depends on the specific situation of your media
                organization and the context in which you are working. We
                recommend filling out "Basic planning" and then continuing with
                the specific topics that are most relevant to your organization.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="How do I save my current status?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                Click under "Get results" on "Save progress". You can download a
                file ending with "resiliencescanner". You can upload this file
                again later under "Continue crisis plan" and all your answers
                will reappear in the tool.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="I closed the browser tab. Where are my answers?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                If you do not save your results under "Save progress" or "Finish
                crisis plan" and get a file ending in "resiliencescanner", all
                your answers will be lost. For security reasons, the Media
                Resilience Scanner does not store any user data on the server.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="I want to update my crisis plan. What do I have to do?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                If you used the Media Resilience Scanner in the past, you have
                to upload the file ending in "resiliencescanner" under "Continue
                crisis plan". You might have downloaded it under "Save
                progress". If you no longer have this file and closed the tab
                from your previous session, you must start from the beginning.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="Why should I trust you? Who is behind the Resilience Scanner?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                The Media Resilience Scanner is a DW Akademie project. DW
                Akademie is part of Deutsche Welle, a public service broadcaster
                and strategic partner of the German Federal Ministry for
                Economic Cooperation and Development. DW Akademie receives
                funding from the German Federal Foreign Office and the European
                Union.
                <br />
                <br />
                The Media Resilience Scanner draws upon lessons learned by over
                30 media organizations in 15 countries, including Myanmar, Iraq,
                the Philippines, El Salvador, Lebanon, the United States and
                Kenya. Managers and journalists who successfully led their
                organizations through internal and external crises, conflicts,
                and natural disasters contributed their experiences and best
                practices to help build this tool.
              </p>
            </Collapsible>
          </div>
          <Link to="/categories">
            <Button type="button" buttonType="secondary">
              Proceed to crisis plan
            </Button>
          </Link>
        </div>
      </Container>
      <Footer />
    </>
  );
};

export default HowItWorksPage;
